/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * HyusnSu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Fri Apr 23 2023
 **/

import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";

import { JotaiStore } from "@/frameworks/libraries/commons";

import { modalListAtom, modalDimensionsAtom, modalEventListAtom } from "./private/atom";

export default function useModal() {
	const show = useAtomCallback(
		useCallback((_, set, data: DesignStuff.Modal.State) => {
			set(modalListAtom, (modalList) => {
				if (modalList.length === 0) return [data];
				return [...modalList, data];
			});
			if (!data.dimensions) return;
			set(modalDimensionsAtom, (prev) => {
				return {
					...prev,
					...data.dimensions
				};
			});
		}, []),

		{ store: JotaiStore }
	);

	const hide = useAtomCallback(
		useCallback((get, set, id: string) => {
			const modalEventList = get(modalEventListAtom);
			if (modalEventList[id]) {
				set(modalEventListAtom, (prev) => {
					prev[id].onClose?.();
					delete prev[id];
					return prev;
				});
			}
			set(modalListAtom, (modalList) => {
				return modalList.filter((item) => item.id !== id);
			});
			set(modalDimensionsAtom, {
				width: "auto",
				height: "auto"
			});
		}, []),
		{ store: JotaiStore }
	);

	const hideAll = useAtomCallback(
		useCallback((get, set) => {
			const modalEventList = get(modalEventListAtom);
			Object.values(modalEventList).forEach((event) => event.onClose?.());
			set(modalEventListAtom, {});
			set(modalListAtom, []);
			set(modalDimensionsAtom, {
				width: "auto",
				height: "auto"
			});
		}, []),
		{ store: JotaiStore }
	);

	const change = useAtomCallback(
		useCallback((_, set, from: string, to: DesignStuff.Modal.State) => {
			set(modalListAtom, (modalList) => {
				const closedModalList = modalList.filter((item) => item.id !== from);

				return modalList?.length === 0 ? [to] : [...closedModalList, to];
			});

			if (!to.dimensions) return;
			set(modalDimensionsAtom, (prev) => {
				return {
					...prev,
					...to.dimensions
				};
			});
		}, []),
		{ store: JotaiStore }
	);

	const setEvents = useAtomCallback(
		useCallback((_, set, id: string, events: DesignStuff.Modal.HandlerEvent) => {
			set(modalEventListAtom, (prev) => {
				return { ...prev, [id]: events };
			});
		}, []),
		{ store: JotaiStore }
	);

	const getAll = useAtomCallback(
		useCallback((get) => {
			return get(modalListAtom);
		}, []),
		{ store: JotaiStore }
	);

	const getLast = useAtomCallback(
		useCallback((get) => {
			const modalList = get(modalListAtom);
			return modalList[modalList.length - 1];
		}, []),
		{ store: JotaiStore }
	);

	/**
	 * @description UI 상 존재하는지 여부를 판단함.
	 */
	const isExistModal = (modalID: string) => {
		const isExist = useAtomCallback(
			useCallback((get) => {
				const modalList = get(modalListAtom);
				return modalList.some((modal) => modal.id === modalID);
			}, []),
			{ store: JotaiStore }
		);
		return isExist;
	};

	return { show, hide, hideAll, change, setEvents, getAll, getLast, isExistModal };
}
